import dayjs from 'dayjs';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Modal, TabItem, Tabs } from '@globalmed/atlas-ui';
import { isAxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { FieldPath, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ConfigurationTab,
  ModalTitle,
  POCTab,
  ProfileTab,
  ProvisioningTab,
} from './components';
import styles from './CreateOrganizationModal.module.css';
import {
  createOrganizationSchema,
  defaultValues,
  mapOrganizationToFormValues,
  orgAdminOrganizationSchema,
  TCreateOrganizationForm,
} from './schema';
import { CREATE_ORGANIZATION_TABS, TCreateOrganizationTab } from './types';
import { ORG_INSTITUTION_PROFILE_FIELDS } from '@/constants';
import { getActiveContractIndex } from './utils/getActiveContractIndex';
import { CloseWarningModal } from '@/components/CloseWarningModal';
import { useOrganizationsStore } from '@/stores/organizations';
import { useToastStore } from '@/stores/toast';
import { useUsersStore } from '@/stores/users';
import {
  EOrganizationProvisioningScope,
  EOrganizationProvisioningType,
  TOrganizationRequestCamel,
} from '@/types';
import { TNullable } from '@/types/common';
import { getDateOnlyISOFormat } from '@/utils/convertDate';
import { hasContractStarted } from '@/utils/organizationContracts';
import { createEnumGuard, createUnionGuard } from '@/utils/typeGuards';

const isCreateOrganizationTab = createUnionGuard(CREATE_ORGANIZATION_TABS);
const isProvisioningScope = createEnumGuard(EOrganizationProvisioningScope);

type TProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  isEditMode?: boolean;
};

/**
 * A modal for creating a new organization, or editing the current one when
 * `isEditMode` is set, with its form split across Profile, Configuration, POC,
 * and Provisioning tabs.
 * @param props - Component props.
 * @param props.open - If `true`, the modal is visible.
 * @param props.setOpen - Callback function to set the open state of the modal.
 * @param props.isEditMode - When `true`, the modal edits the current
 * organization instead of creating a new one.
 * @returns JSX.Element representing the create/edit organization modal.
 */
export const CreateOrganizationModal = ({
  open,
  setOpen,
  isEditMode = false,
}: TProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TCreateOrganizationTab>('profile');
  const [isCloseWarningOpen, setIsCloseWarningOpen] = useState(false);
  const [loadedFormValues, setLoadedFormValues] =
    useState<TNullable<TCreateOrganizationForm>>(null);
  const isSuperAdmin = useUsersStore((state) => state.checkIsSuperAdmin());
  const isLockedForOrgAdmin = isEditMode && !isSuperAdmin;

  const { currentOrganization, createOrganization, updateOrganization } =
    useOrganizationsStore((state) => ({
      currentOrganization: state.currentOrganization,
      createOrganization: state.createOrganization,
      updateOrganization: state.updateOrganization,
    }));
  const { setToast } = useToastStore();
  const occupiedSeats = isEditMode
    ? (currentOrganization?.provisioning?.occupiedSeats ?? undefined)
    : undefined;

  const schema = isLockedForOrgAdmin
    ? orgAdminOrganizationSchema
    : createOrganizationSchema;

  const formMethods = useForm<TCreateOrganizationForm>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    control,
    reset,
    getValues,
    setValue,
    setError,
    handleSubmit: handleFormSubmit,
    formState: { isDirty, isSubmitting, errors, dirtyFields },
  } = formMethods;

  useEffect(() => {
    if (!open || !isEditMode) return;

    const organization = useOrganizationsStore.getState().currentOrganization;
    if (!organization) return;

    const mapped = mapOrganizationToFormValues(organization);
    reset(mapped);
    setLoadedFormValues(mapped);
  }, [open, isEditMode, reset]);

  const values = useWatch({ control });
  const invalidFields = new Set([
    ...(schema.safeParse(values).error?.issues.map(({ path }) => path[0]) ??
      []),
    ...Object.keys(errors),
  ]);

  const closeModal = (shouldReset: boolean) => {
    if (shouldReset && !isEditMode) reset(defaultValues);
    setActiveTab('profile');
    setOpen(false);
  };

  const isOnlySeatsChanged = () => {
    const { provisioning, ...otherFields } = dirtyFields;
    const { contracts = [], ...otherProvisioning } = provisioning ?? {};
    return (
      !Object.keys(otherFields).length &&
      !Object.keys(otherProvisioning).length &&
      contracts.some((contract) => !!contract?.seats) &&
      contracts.every((contract) =>
        Object.keys(contract ?? {}).every((key) => key === 'seats'),
      )
    );
  };

  const showServerError = (
    tab: TCreateOrganizationTab,
    name: FieldPath<TCreateOrganizationForm>,
    message: string,
  ) => {
    setActiveTab(tab);
    setError(name, { type: 'server', message });
  };

  const restoreStartedContracts = () => {
    getValues('provisioning.contracts').forEach(({ id }, index) => {
      const savedContract = loadedFormValues?.provisioning.contracts.find(
        (contract) => !!id && contract.id === id,
      );
      if (
        !savedContract ||
        !hasContractStarted(savedContract.contractStartDate)
      ) {
        return;
      }
      setValue(`provisioning.contracts.${index}`, savedContract, {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
  };

  const handleSubmit = async (data: TCreateOrganizationForm) => {
    const { configuration, provisioning, ...rest } = data;
    const { type, scope, contracts } = provisioning;
    const isConsults =
      type === EOrganizationProvisioningType.CONSULTS_PER_MONTH;
    const payload: TOrganizationRequestCamel = {
      ...rest,
      ...(!isLockedForOrgAdmin && {
        configuration,
        provisioning: {
          type,
          scope: isProvisioningScope(scope) ? scope : undefined,
          contracts: contracts
            .filter((contract) => !!contract.id || !!contract.contractStartDate)
            .map((contract) => ({
              id: contract.id,
              seats:
                !isConsults && contract.seats
                  ? Number(contract.seats)
                  : undefined,
              stationLicenses:
                !isConsults && contract.stationLicenses
                  ? Number(contract.stationLicenses)
                  : undefined,
              consultsPerMonth:
                isConsults && contract.consultsPerMonth
                  ? Number(contract.consultsPerMonth)
                  : undefined,
              contractStartDate: contract.contractStartDate
                ? getDateOnlyISOFormat(dayjs(contract.contractStartDate))
                : undefined,
              contractEndDate:
                !isConsults && contract.contractEndDate
                  ? getDateOnlyISOFormat(dayjs(contract.contractEndDate))
                  : undefined,
            })),
        },
      }),
    };

    try {
      if (isEditMode && currentOrganization) {
        await updateOrganization(currentOrganization.id, payload);
        setToast({
          title: t(
            isOnlySeatsChanged()
              ? 'create-organization-modal.seats-provisioned-updated'
              : 'create-organization-modal.organization-updated',
          ),
          variant: 'success',
        });
      } else {
        await createOrganization(payload);
        setToast({
          title: t('create-organization-modal.organization-created'),
          variant: 'success',
        });
      }
      closeModal(true);
    } catch (error) {
      const apiError = isAxiosError(error)
        ? String(error.response?.data?.error ?? '')
        : '';
      const field = apiError.replace(/^bad_params:/, '');
      const reason: string | undefined = isAxiosError(error)
        ? error.response?.data?.meta?.fields?.[field]?.[0]
        : undefined;

      if (apiError === 'bad_params:name') {
        showServerError(
          'profile',
          'name',
          'create-organization-modal.organization-name-already-exists',
        );
        return;
      }
      if (
        field === 'provisioning.contracts.seats' &&
        reason === 'below_occupied'
      ) {
        showServerError(
          'provisioning',
          `provisioning.contracts.${getActiveContractIndex(contracts)}.seats`,
          'create-organization-modal.seats-provisioned-cannot-be-lower-than-occupied',
        );
        return;
      }
      if (field.startsWith('provisioning.contracts.') && reason === 'locked') {
        restoreStartedContracts();
        setActiveTab('provisioning');
      }
      setToast({
        title: t('something-went-wrong-please-try-again'),
        variant: 'error',
      });
      console.error('Create organization failed:', error);
    }
  };

  const items: TabItem[] = [
    {
      key: 'profile',
      title: t('admin-panel.profile'),
      alert: ORG_INSTITUTION_PROFILE_FIELDS.some((field) =>
        invalidFields.has(field),
      ),
    },
    {
      key: 'configuration',
      title: t('admin-panel.configuration'),
      alert: invalidFields.has('configuration'),
    },
    {
      key: 'poc',
      title: t('create-organization-modal.poc'),
      alert: invalidFields.has('pocSettings'),
    },
    {
      key: 'provisioning',
      title: t('create-organization-modal.provisioning'),
      alert: invalidFields.has('provisioning'),
    },
  ];

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setOpen(true);
      return;
    }
    if (isDirty) {
      setIsCloseWarningOpen(true);
      return;
    }
    closeModal(false);
  };

  return (
    <>
      <Modal
        open={open && !isCloseWarningOpen}
        onOpenChange={handleOpenChange}
        sx={{ width: '600px' }}
        title={<ModalTitle isEditMode={isEditMode} />}
        body={
          <FormProvider {...formMethods}>
            <form
              className={styles.form}
              onSubmit={handleFormSubmit(handleSubmit)}
            >
              <Tabs
                items={items}
                variant="underline"
                size="sm"
                activeKey={activeTab}
                onChange={(item) => {
                  if (isCreateOrganizationTab(item.key)) setActiveTab(item.key);
                }}
              />
              {activeTab === 'profile' && <ProfileTab />}
              {activeTab === 'configuration' && (
                <ConfigurationTab disabled={isLockedForOrgAdmin} />
              )}
              {activeTab === 'poc' && <POCTab />}
              {activeTab === 'provisioning' && (
                <ProvisioningTab
                  disabled={isLockedForOrgAdmin}
                  isTypeLocked={
                    isEditMode && !!currentOrganization?.provisioning?.type
                  }
                  isScopeLocked={
                    isEditMode && !!currentOrganization?.provisioning?.scope
                  }
                  savedContracts={loadedFormValues?.provisioning.contracts}
                  occupiedSeats={occupiedSeats}
                />
              )}

              <div className={styles.buttons}>
                <Button
                  preIcon={isEditMode ? undefined : 'Plus'}
                  text={t(
                    isEditMode
                      ? 'create-organization-modal.save-and-close'
                      : 'nav-panel.create-organization',
                  )}
                  disabled={!!invalidFields.size || (isEditMode && !isDirty)}
                  loading={isSubmitting}
                  isSubmit
                />
              </div>
            </form>
          </FormProvider>
        }
      />
      <CloseWarningModal
        isOpen={isCloseWarningOpen}
        description={t('create-organization-modal.close-warning-description')}
        onCancel={() => {
          setIsCloseWarningOpen(false);
          setOpen(true);
        }}
        onConfirm={() => {
          setIsCloseWarningOpen(false);
          closeModal(true);
        }}
      />
    </>
  );
};
